import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Target, BookOpen, FileText, BarChart3, ChevronRight, Radio, Info, Shield, Zap, Users } from "lucide-react";
import api from "@/lib/api";
import useSEO from "@/hooks/useSEO";
import { istDateStr, readCache, writeCache } from "@/lib/ist";

const WHATSAPP_URL = "https://whatsapp.com/channel/0029Vb8MzS9F1YlWpvY4M81x";
const TODAY_CACHE_KEY = "jt_today_v1";

function formatDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
}

function ResultBox({ label, time, value, testId }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="text-center">
        <div className="font-bold text-sm sm:text-base tracking-wide text-[color:var(--brand-purple-deep)]">{label}</div>
        <div className="text-xs sm:text-sm text-[color:var(--brand-purple)]/80 font-semibold">{time}</div>
      </div>
      <div className="result-num-box w-full" data-testid={testId}>
        <div className="result-num">{value == null ? "XX" : String(value).padStart(2, "0")}</div>
      </div>
    </div>
  );
}

function FeatureCard({ to, title, subtitle, tone, icon, cta }) {
  const tones = {
    purple: { bg: "linear-gradient(180deg,#f5efff 0%,#ffffff 60%)", btn: "btn-purple", ring: "ring-[color:var(--brand-purple)]/20" },
    orange: { bg: "linear-gradient(180deg,#fff2e5 0%,#ffffff 60%)", btn: "btn-orange", ring: "ring-orange-200" },
    green: { bg: "linear-gradient(180deg,#e8f7ee 0%,#ffffff 60%)", btn: "bg-emerald-600 text-white rounded-xl px-3 py-2 font-bold inline-flex items-center gap-1", ring: "ring-emerald-200" },
  }[tone];
  return (
    <Link to={to} className="feature-card group ring-1 hover:ring-2 transition" style={{ background: tones.bg }} data-testid={`feature-card-${title.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-sm">
        {icon}
      </div>
      <div className="text-center px-1">
        <div className={`font-display font-extrabold text-[15px] sm:text-base ${tone === "orange" ? "text-orange-600" : tone === "green" ? "text-emerald-700" : "text-[color:var(--brand-purple-deep)]"}`}>{title}</div>
        <div className="text-[11px] sm:text-xs text-[color:var(--brand-purple)]/80 mt-1 leading-snug">{subtitle}</div>
      </div>
      <div className={`w-full ${tones.btn} justify-between rounded-b-none rounded-t-none mt-2 py-2.5 text-sm`}>
        <span className="mx-auto flex items-center gap-1">{cta}<ChevronRight size={16} /></span>
      </div>
    </Link>
  );
}

function TrustBadge({ icon, title, subtitle, color }) {
  return (
    <div className="flex flex-col items-center text-center gap-1 p-2">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${color}`}>{icon}</div>
      <div className="text-xs font-bold text-[color:var(--brand-purple-deep)]">{title}</div>
      <div className="text-[11px] text-[color:var(--brand-purple)]/80 leading-tight">{subtitle}</div>
    </div>
  );
}

export default function Home() {
  // Instant paint: read cache synchronously (still same IST date only)
  const [today, setToday] = useState(() => {
    const cached = readCache(TODAY_CACHE_KEY);
    if (cached && cached.date === istDateStr()) return cached;
    return null;
  });

  useSEO({
    title: "Juwai Teer | Juwai Teer Result | Juwai Teer Result Today | FR & SR Live Result",
    description: "Check Juwai Teer Result Today with the latest FR and SR results, previous results, common numbers, result timings and Juwai Teer information.",
    canonical: "https://jowaiteer.com/",
  });

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const { data } = await api.get("/results/today");
        if (!mounted) return;
        setToday(data);
        // Persist so next refresh paints instantly (but only for the same IST date)
        writeCache(TODAY_CACHE_KEY, data);
      } catch (_e) {
        // Silent — display "--"
      }
    }
    load();
    const poll = setInterval(load, 30000);
    // Revalidate immediately when the tab comes back to foreground
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { mounted = false; clearInterval(poll); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  const dateLabel = today ? formatDate(today.date) : "";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6 pb-4">
      {/* Result section */}
      <section className="text-center rise-in">
        <div className="ornament justify-center mb-3">
          <span className="line" /><span className="arrow" /><span className="uppercase text-xs font-bold tracking-widest text-[color:var(--brand-orange)]">Live Now</span><span className="arrow" /><span className="line" />
        </div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[color:var(--brand-purple-deep)] tracking-tight">
          JUWAI TEER RESULT TODAY
        </h1>
        <div className="mt-2 inline-flex items-center gap-2 text-sm sm:text-base font-semibold text-[color:var(--brand-purple)]">
          <span className="inline-block w-4 h-4 rounded-sm border-2 border-[color:var(--brand-purple)]" />
          <span data-testid="today-date-label">{dateLabel || "—"}</span>
        </div>
      </section>

      <section className="card-soft mt-5 p-5 sm:p-7 rise-in rise-in-delay-1" data-testid="today-result-card">
        <div className="flex justify-center">
          <div className="btn-purple text-sm rounded-full px-4 py-2">
            <Radio size={16} /> TODAY&apos;S LIVE RESULT
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:gap-8 mt-5 divide-x divide-[hsl(var(--border))]">
          <div className="pr-2 sm:pr-6">
            <ResultBox label="FIRST ROUND (FR)" time="2:10 PM" value={today?.fr ?? null} testId="fr-result" />
          </div>
          <div className="pl-2 sm:pl-6">
            <ResultBox label="SECOND ROUND (SR)" time="3:10 PM" value={today?.sr ?? null} testId="sr-result" />
          </div>
        </div>
        <div className="mt-5 flex justify-center">
          <Link to="/previous-result/" className="btn-purple w-full sm:w-auto" data-testid="view-previous-results-btn">
            <BarChart3 size={18} /> VIEW PREVIOUS RESULTS
          </Link>
        </div>
      </section>

      {/* Three feature cards */}
      <section className="grid grid-cols-3 gap-3 sm:gap-5 mt-5 rise-in rise-in-delay-2">
        <FeatureCard
          to="/common-number/"
          title="COMMON NUMBER"
          subtitle="Daily Common Numbers Chart"
          tone="purple"
          icon={<Target className="text-[color:var(--brand-purple)]" />}
          cta="VIEW NOW"
        />
        <FeatureCard
          to="/juwai-teer-guide/"
          title="JUWAI TEER GUIDE"
          subtitle="Game Info, Rules, Time & More"
          tone="orange"
          icon={<BookOpen className="text-orange-500" />}
          cta="EXPLORE"
        />
        <FeatureCard
          to="/previous-result/"
          title="PREVIOUS RESULT"
          subtitle="Check All Previous FR & SR"
          tone="green"
          icon={<FileText className="text-emerald-600" />}
          cta="VIEW NOW"
        />
      </section>

      {/* About with illustration */}
      <section className="card-soft mt-5 p-5 sm:p-7 rise-in rise-in-delay-3" aria-labelledby="about-heading">
        <div className="grid sm:grid-cols-2 gap-5 items-center">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-full bg-[color:var(--brand-purple-soft)] flex items-center justify-center"><Info size={18} className="text-[color:var(--brand-purple)]" /></div>
              <h2 id="about-heading" className="font-display font-extrabold text-xl text-[color:var(--brand-purple-deep)]">About Juwai Teer</h2>
            </div>
            <div className="h-[3px] w-16 rounded-full bg-[color:var(--brand-orange)] mb-3" />
            <p className="text-sm leading-relaxed text-[color:var(--brand-purple)]/95">
              Juwai Teer is a traditional archery-based activity historically associated with the
              Jaintia Hills region of Meghalaya, India. It is played in two rounds each day —
              First Round (FR) at 2:10 PM and Second Round (SR) at 3:10 PM. This website provides
              result information and general educational content about Juwai Teer.
            </p>
          </div>
          <div>
            <svg viewBox="0 0 400 220" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Meghalaya hills illustration">
              <defs>
                <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f5efff"/><stop offset="100%" stopColor="#ffffff"/></linearGradient>
                <linearGradient id="hill1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7a4bb0"/><stop offset="100%" stopColor="#5b2a86"/></linearGradient>
                <linearGradient id="hill2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#a781d4"/><stop offset="100%" stopColor="#7a4bb0"/></linearGradient>
              </defs>
              <rect width="400" height="220" fill="url(#sky)" rx="14" />
              <circle cx="310" cy="60" r="26" fill="#f97316"/>
              <path d="M40 40 q10 -10 20 0" stroke="#a781d4" strokeWidth="2" fill="none"/>
              <path d="M80 30 q10 -10 20 0" stroke="#a781d4" strokeWidth="2" fill="none"/>
              <path d="M0 160 Q80 90 160 140 T320 130 T400 150 L400 220 L0 220 Z" fill="url(#hill2)"/>
              <path d="M0 180 Q100 130 200 170 T400 175 L400 220 L0 220 Z" fill="url(#hill1)"/>
              <rect x="150" y="150" width="12" height="30" fill="#3d1a68"/>
              <rect x="240" y="150" width="12" height="30" fill="#3d1a68"/>
              <rect x="150" y="145" width="102" height="10" fill="#3d1a68"/>
              <g fill="#22c55e"><circle cx="70" cy="175" r="10"/><circle cx="60" cy="180" r="8"/><circle cx="330" cy="180" r="10"/><circle cx="345" cy="185" r="8"/></g>
            </svg>
          </div>
        </div>
      </section>

      {/* WhatsApp banner */}
      <section className="mt-5 rise-in">
        <div className="card-soft p-4 sm:p-5 flex items-center gap-3 sm:gap-4" style={{ background: "linear-gradient(90deg,#f5efff 0%,#ffffff 60%)" }}>
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white flex items-center justify-center shadow-sm">
            <Target className="text-[color:var(--brand-purple)]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-extrabold text-sm sm:text-base text-[color:var(--brand-purple-deep)]">GET DAILY JUWAI TEER UPDATES</div>
            <div className="text-[11px] sm:text-xs text-[color:var(--brand-purple)]/80">Join our WhatsApp Channel &amp; never miss a result</div>
          </div>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-purple text-xs sm:text-sm"
            data-testid="whatsapp-join-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.5 0 .17 5.34.17 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.32-1.65a11.86 11.86 0 0 0 5.74 1.47h.01c6.56 0 11.9-5.34 11.9-11.9 0-3.18-1.24-6.17-3.45-8.44ZM12.07 21.4c-1.86 0-3.68-.5-5.27-1.44l-.38-.22-3.75.98 1-3.66-.25-.38a9.5 9.5 0 1 1 17.65-5.0 9.5 9.5 0 0 1-9 9.71Zm5.44-7.1c-.3-.15-1.77-.87-2.05-.97-.28-.1-.48-.15-.68.15-.2.3-.78.97-.96 1.17-.18.2-.35.22-.65.07-.3-.15-1.27-.47-2.42-1.5-.9-.8-1.5-1.79-1.68-2.09-.18-.3-.02-.47.13-.62.13-.13.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.68-1.63-.93-2.24-.24-.58-.5-.5-.68-.5h-.58c-.2 0-.52.08-.8.37-.28.3-1.05 1.02-1.05 2.5s1.07 2.9 1.22 3.1c.15.2 2.1 3.2 5.1 4.49.72.31 1.28.5 1.71.63.72.23 1.37.2 1.88.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.29.17-1.42-.07-.13-.28-.2-.58-.35Z"/></svg>
            JOIN NOW
          </a>
        </div>
      </section>

      {/* Trust row */}
      <section className="grid grid-cols-4 gap-2 sm:gap-4 mt-5 rise-in">
        <TrustBadge icon={<Shield className="text-[color:var(--brand-purple)]" size={18}/>} title="Reliable Updates" subtitle="Fast & consistent" color="bg-[color:var(--brand-purple-soft)]" />
        <TrustBadge icon={<Zap className="text-orange-500" size={18}/>} title="Real-Time" subtitle="Live update after result" color="bg-orange-100" />
        <TrustBadge icon={<FileText className="text-emerald-600" size={18}/>} title="Result Archive" subtitle="Full previous history" color="bg-emerald-100" />
        <TrustBadge icon={<Users className="text-[color:var(--brand-purple)]" size={18}/>} title="Mobile Friendly" subtitle="Simple & clean" color="bg-[color:var(--brand-purple-soft)]" />
      </section>

      {/* Content */}
      <section className="mt-8 grid gap-5">
        <div className="card-soft p-5 sm:p-7">
          <h2 className="font-display text-xl sm:text-2xl font-extrabold text-[color:var(--brand-purple-deep)]">What is Juwai Teer?</h2>
          <div className="h-[3px] w-16 rounded-full bg-[color:var(--brand-orange)] mt-2 mb-3" />
          <p className="text-sm leading-relaxed text-[color:var(--brand-purple)]/95">
            Juwai Teer is an archery-based traditional activity associated with the Jaintia Hills district of Meghalaya.
            Two rounds are conducted daily and the results are announced publicly. This website records those
            announced results and makes them easy to look up on mobile.
          </p>
        </div>

        <div className="card-soft p-5 sm:p-7">
          <h2 className="font-display text-xl sm:text-2xl font-extrabold text-[color:var(--brand-purple-deep)]">How to check today&apos;s Juwai Teer result</h2>
          <div className="h-[3px] w-16 rounded-full bg-[color:var(--brand-orange)] mt-2 mb-3" />
          <ol className="list-decimal ml-5 text-sm leading-relaxed text-[color:var(--brand-purple)]/95 space-y-1">
            <li>Open <strong>jowaiteer.com</strong> on your phone.</li>
            <li>Today&apos;s FR result appears in the live card at/after 2:10 PM.</li>
            <li>SR result appears at/after 3:10 PM.</li>
            <li>Tap <em>View Previous Results</em> for the full archive.</li>
          </ol>
        </div>

        <div className="card-soft p-5 sm:p-7">
          <h2 className="font-display text-xl sm:text-2xl font-extrabold text-[color:var(--brand-purple-deep)]">Frequently Asked Questions</h2>
          <div className="h-[3px] w-16 rounded-full bg-[color:var(--brand-orange)] mt-2 mb-4" />
          <div className="space-y-4 text-sm">
            <div>
              <div className="font-bold text-[color:var(--brand-purple-deep)]">What time is Juwai Teer FR result?</div>
              <div className="text-[color:var(--brand-purple)]/90">First Round is announced at 2:10 PM.</div>
            </div>
            <div>
              <div className="font-bold text-[color:var(--brand-purple-deep)]">What time is Juwai Teer SR result?</div>
              <div className="text-[color:var(--brand-purple)]/90">Second Round is announced at 3:10 PM.</div>
            </div>
            <div>
              <div className="font-bold text-[color:var(--brand-purple-deep)]">Where can I check previous results?</div>
              <div className="text-[color:var(--brand-purple)]/90">Open the <Link to="/previous-result/" className="link-underline font-semibold">Previous Result</Link> page for the full archive.</div>
            </div>
            <div>
              <div className="font-bold text-[color:var(--brand-purple-deep)]">Are common numbers guaranteed?</div>
              <div className="text-[color:var(--brand-purple)]/90">No. Common numbers are reference data for informational purposes only and are not predictions or guarantees.</div>
            </div>
          </div>

          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context":"https://schema.org",
            "@type":"FAQPage",
            "mainEntity":[
              {"@type":"Question","name":"What time is Juwai Teer FR result?","acceptedAnswer":{"@type":"Answer","text":"First Round is announced at 2:10 PM."}},
              {"@type":"Question","name":"What time is Juwai Teer SR result?","acceptedAnswer":{"@type":"Answer","text":"Second Round is announced at 3:10 PM."}},
              {"@type":"Question","name":"Where can I check previous results?","acceptedAnswer":{"@type":"Answer","text":"Open the Previous Result page for the full archive."}},
              {"@type":"Question","name":"Are common numbers guaranteed?","acceptedAnswer":{"@type":"Answer","text":"No. Common numbers are reference data for informational purposes only and are not predictions or guarantees."}}
            ]
          })}} />
        </div>
      </section>
    </div>
  );
}
